import { useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { State } from "store/reducers";
import ls from "Localization";
import _ from "lodash";
import { Helmet } from "react-helmet-async";

import actions from "store/actions";

import { TransferState } from "store/reducers/transfer";
import ITransfer, { TransferStatus, TransferType } from "Types/Transfer";
import dayjs from "dayjs";
import Report from "Components/Report";
import { dateSort } from "Components/Report/helpers";
import { toStringCurrency } from "helpers/string";

import ImportModal from "./Import";
import ReactTable from "react-table";
import { FaExternalLinkAlt } from "react-icons/fa";
import { MdAdd } from "react-icons/md";
import { Fab } from "@mui/material";
import { useNavigate } from "react-router-dom";
import useStyles from "./styles";

const columns = [
	{
		Header: ls.creationDate,
		id: "createdAt",
		accessor: (c: ITransfer) =>
			dayjs(c.createdAt).format(ls.dateFormatWithoutSeconds),
		show: true,
		sortMethod: dateSort,
		width: 150
	},
	{
		Header: ls.client,
		id: "user",
		show: true,
		filterable: false,
		accessor: (c: ITransfer) => `${c.user.name} ${c.user.lastname}`
	},
	{
		Header: "Documento",
		id: "to",
		accessor: (c: any) => c.to,
		show: true,
		filterable: false,
		width: 150
	},
	{
		Header: ls.value,
		id: "value",
		show: true,
		filterable: false,
		accessor: (c: ITransfer) => toStringCurrency(+c.value / 100.0),
		width: 150
	},
	{
		Header: ls.type,
		id: "type",
		show: true,
		accessor: (c: ITransfer) => ls[TransferType[c.type]],
		width: 140
	},
	{
		Header: ls.status,
		id: "status",
		show: true,
		accessor: (c: ITransfer) => ls[TransferStatus[c.status]],
		width: 140
	},
	{
		Header: ls.actions,
		id: "actions",
		accessor: (c: any) => (
			<div
				style={{
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					flex: 1
				}}
			>
				<a href={c.url} target="_blank">
					<FaExternalLinkAlt />
				</a>
			</div>
		),
		show: true,
		filterable: false,
		width: 80
	}
];

let getTransfersDebounded: (() => any) & _.Cancelable;

interface TableState {
	page: number;
	pageSize: number;
	sorted: any[];
	filtered: any[];
	toExport: boolean;
	callback: any;
}

function Transfers() {
	const navigate = useNavigate();
	const dispatch = useDispatch<any>();
	const table: any = useRef<ReactTable<any>>(null);
	const classes = useStyles({});

	const state = useSelector<State, TransferState>(s => s.transfer);

	const handleFetchData = (tableState: TableState, instance: any) => {
		if (!tableState) return;
		const { page, pageSize, toExport, callback } = tableState;

		if (getTransfersDebounded) {
			getTransfersDebounded.cancel();
		}

		if (!tableState.sorted) {
			tableState.sorted = [];
		}

		const creationDate = tableState.filtered.find(
			(c: any) => c.id === "creationDate"
		);

		if (creationDate?.value) {
			tableState.filtered = tableState.filtered.filter(
				(c: any) => c.id !== "creationDate"
			);

			if (creationDate?.value.startDate) {
				tableState.filtered.push({
					id: "startDate",
					value: creationDate.value.startDate
				});
			}

			if (creationDate?.value.endDate) {
				tableState.filtered.push({
					id: "endDate",
					value: creationDate.value.endDate
				});
			}
		}

		getTransfersDebounded = _.debounce(
			() =>
				dispatch(
					actions.transfer.getTransfers(
						page * pageSize,
						pageSize,
						tableState.filtered.reduce(
							(p: any, c: any) => p + `&filters[${c.id}]=${c.value}`,
							""
						),
						tableState.sorted[0]?.id,
						tableState.sorted[0]?.desc,
						toExport,
						callback
					)
				),
			500
		);

		getTransfersDebounded();
	};

	const load = () => handleFetchData(table.current?.state, null);

	return (
		<div>
			<Helmet>
				<title>
					{ls.transfers} | {ls.appName}
				</title>
			</Helmet>
			<Report
				titleText=""
				manual
				tableRef={table}
				headerRightComponent={<ImportModal load={load} />}
				title={ls.transfers}
				data={state.items}
				pages={state.pages}
				loading={state.loading.getAll}
				columns={columns}
				onFetchData={handleFetchData}
				filterable
				visibleColumns={columns.filter(c => c.show).map(c => c.Header)}
				defaultSorted={[
					{
						id: "createdAt",
						desc: true
					}
				]}
				getTdProps={() => {
					return {
						style: {
							display: "flex",
							alignItems: "center"
						}
					};
				}}
				onRowClicked={data => navigate(`/Dashboard/Transfer/${data._id}`)}
			/>

			<Fab
				color="primary"
				aria-label="Add"
				style={{ position: 'fixed', bottom: '16px', right: '16px',  zIndex: 1000}}
				onClick={() => navigate("/Dashboard/Transfer/Create")}
			>
				<MdAdd size={24} />
			</Fab>
		</div>
	);
}

export default Transfers;
